import { useEffect, useMemo, useState } from "react";
import type { SessionInfo } from "../../shared/auth";
import { heldTeamIds } from "../../shared/team-access";
import type { Team } from "../../shared/types";

type SessionModule = typeof import("@/lib/session");

/** The session module once some page has loaded it: read synchronously on a
 *  later mount, never imported statically. */
let loaded: SessionModule | null = null;

function activeInfo(module: SessionModule | null): SessionInfo | null {
  const state = module?.settledSession() ?? null;
  return state?.status === "active" ? state.info : null;
}

/**
 * The teams of this tournament the browser's credentials make its own
 * (`shared/team-access.ts`), for a PUBLIC page: the board and a team's page
 * offer a shortcut to the team workspace with it.
 *
 * Those pages live in the main chunk, so the session module is imported on
 * demand, exactly like `AccountLink`: the capability tables stay out of a QR
 * landing's first download. `ensureSession()` caches one promise per page
 * load, and the header's account link has already asked, so this adds no
 * request. The first answer arrives after the first paint (the shortcut
 * appears late the way "My account" replaces "Log in"); every later mount in
 * the same page load starts from the settled answer, so going back to the
 * board does not make its tabs jump.
 *
 * An "offline" answer is asked again when the device comes back online or the
 * page is shown again: `ensureSession` does not cache it, but a board stays
 * open for hours and nothing else here would ask.
 */
export function useHeldTeams(tournamentId: string, teams: readonly Team[]): string[] {
  const [info, setInfo] = useState<SessionInfo | null>(() => activeInfo(loaded));
  useEffect(() => {
    let alive = true;
    let waiting = false;
    const ask = (): void => {
      void import("@/lib/session")
        .then(async (module) => {
          loaded = module;
          const state = await module.ensureSession();
          if (!alive) return;
          waiting = state.status === "offline";
          if (!waiting) setInfo(state.status === "active" ? state.info : null);
        })
        .catch(() => {
          waiting = true;
        });
    };
    const retry = (): void => {
      if (waiting && document.visibilityState === "visible") ask();
    };
    ask();
    window.addEventListener("online", retry);
    document.addEventListener("visibilitychange", retry);
    return () => {
      alive = false;
      window.removeEventListener("online", retry);
      document.removeEventListener("visibilitychange", retry);
    };
  }, []);
  // The board re-renders on every socket frame; the fold only when its inputs change.
  return useMemo(
    () => (info === null ? [] : heldTeamIds(info.identities ?? [], tournamentId, teams)),
    [info, tournamentId, teams],
  );
}
